import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Chip, Grid, Skeleton, Button, Card, CardContent, Stack, Divider,
  Tabs, Tab, useMediaQuery, useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useGetMyStudentDetailQuery, useGetMyStudentReportCardQuery } from "../../Api/attoDeskApi";
import StaffStudentCalendar from "./StaffStudentCalendar";

/**
 * Staff Portal → Student Detail (premium redesign).
 *
 * Visual grammar mirrors the Admin student profile (navy hero, KPI
 * strip, tab row, dashboard + quick stats sidebar) but every data
 * source is Staff-scoped:
 *   - useGetMyStudentDetailQuery: staff-scope teaching relationship,
 *     attendance summary, recent classes with me.
 *   - useGetMyStudentReportCardQuery: composed academic summary
 *     (subjects, assessments, feedback) via the report-card endpoint
 *     added earlier. Backend enforces canAccessStudent on both.
 *
 * NO payments, NO billing, NO admin-only editing controls — this is a
 * teacher workspace, not a management surface.
 */

const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";
const PAGE_BG = "#f4f7fb";

interface Detail {
  student_id: number;
  student_name: string;
  grade: string | null;
  avatar_initials: string;
  image: string | null;
  my_relationship: { subjects: string[]; classes_count: number; occurrences_count: number };
  attendance_summary: { present: number; absent: number; late: number; total: number; rate: number };
  recent_classes: Array<{
    occurrence_id: number; class_label: string;
    scheduled_start_at: string; scheduled_end_at: string;
    location: string | null; status: string; actual_end_at: string | null;
  }>;
}

type ReportCard = {
  summary: {
    overall_average: number | null;
    graded_count: number;
    passed_count: number;
    pending_count: number;
    subjects_count: number;
    attendance_rate: number | null;
  };
  subjects: Array<{
    subject: string; assessments: number; passed: number; failed: number;
    average: number | null;
    latest: { date: string | null; score: number | null; max_score: number | null; percentage: number | null } | null;
  }>;
  assessments: Array<{
    assessment_id: number; date: string | null; subject: string; title: string;
    type: string | null; score: number | null; max_score: number | null;
    percentage: number | null; marked: boolean; result_status: string | null;
    pass_fail: boolean | null; teacher: string | null;
  }>;
  attendance: { scheduled: number; present: number; absent: number; late: number; not_marked: number; attendance_rate: number | null };
};

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const fmtTime = (iso: string | null) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const rateColor = (r: number | null | undefined) =>
  r === null || r === undefined ? "#94a3b8" : r >= 80 ? "#16a34a" : r >= 50 ? "#f59e0b" : "#ef4444";

type TabKey = "dashboard" | "profile" | "subjects" | "schedule" | "attendance" | "assessments" | "messages" | "report-card";
const TABS: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
  { key: "dashboard", label: "Dashboard", icon: <DashboardOutlinedIcon fontSize="small" /> },
  { key: "profile", label: "Profile", icon: <PersonOutlineIcon fontSize="small" /> },
  { key: "subjects", label: "Subjects", icon: <MenuBookOutlinedIcon fontSize="small" /> },
  { key: "schedule", label: "Schedule", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { key: "attendance", label: "Attendance", icon: <HowToRegOutlinedIcon fontSize="small" /> },
  { key: "assessments", label: "Assessments", icon: <AssignmentTurnedInOutlinedIcon fontSize="small" /> },
  { key: "messages", label: "Messages", icon: <ChatBubbleOutlineIcon fontSize="small" /> },
  { key: "report-card", label: "Report Card", icon: <DescriptionOutlinedIcon fontSize="small" /> },
];

const KpiCard: React.FC<{ label: string; value: React.ReactNode; sublabel?: string; accent: string; bg: string; icon: React.ReactNode }> = ({ label, value, sublabel, accent, bg, icon }) => (
  <Card sx={{ p: 2, borderRadius: 2.5, border: "1px solid #e2e8f0", height: "100%", position: "relative", overflow: "hidden",
    "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: accent },
  }}>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: bg, color: accent, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.15 }}>{value}</Typography>
    {sublabel && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>{sublabel}</Typography>}
  </Card>
);

const SectionCard: React.FC<{ title: string; icon?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; sx?: any }> = ({ title, icon, right, children, sx }) => (
  <Card sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)", ...sx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon && <Box sx={{ color: NAVY, display: "flex" }}>{icon}</Box>}
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</Typography>
      </Stack>
      {right}
    </Stack>
    {children}
  </Card>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  const conf = s === "completed"
    ? { bg: "#dcfce7", fg: "#166534", label: "Completed" }
    : s === "cancelled"
    ? { bg: "#fee2e2", fg: "#991b1b", label: "Cancelled" }
    : s === "live"
    ? { bg: "#fef3c7", fg: "#92400e", label: "Live" }
    : { bg: "#eef2ff", fg: NAVY, label: "Scheduled" };
  return <Chip size="small" label={conf.label} sx={{ bgcolor: conf.bg, color: conf.fg, fontWeight: 800, fontSize: 10.5, height: 22 }} />;
};

const StudentView: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { studentId } = useParams<{ studentId: string }>();
  const [tab, setTab] = useState<TabKey>("dashboard");

  const { data, isLoading, error } = useGetMyStudentDetailQuery(
    { studentId: studentId ?? "" },
    { skip: !studentId }
  );
  const detail: Detail | null = useMemo(() => (data as any)?.data ?? null, [data]);
  const isForbidden = (error as any)?.status === 403;
  const isMissing = (error as any)?.status === 404;

  // Composed academic summary for the KPIs + report/assessment/subject
  // sections. Skipped until we know the id is valid + accessible.
  const sidNum = Number(studentId);
  const { data: reportResp } = useGetMyStudentReportCardQuery(
    { studentId: sidNum || 0 },
    { skip: !sidNum || Number.isNaN(sidNum) || isForbidden || isMissing },
  );
  const report = ((reportResp as any)?.data as ReportCard | undefined);

  // Hooks MUST run in the same order on every render, so all useMemo
  // calls stay above any early-return branches. They safely handle the
  // no-detail / no-report case internally.
  const att = detail?.attendance_summary;
  const past = useMemo(() => (detail?.recent_classes ?? []).filter((c) => new Date(c.scheduled_start_at).getTime() < Date.now()).slice(0, 6), [detail]);
  const recentAssessment = useMemo(() => {
    if (!report) return null;
    return report.assessments.find((a) => a.marked) ?? null;
  }, [report]);

  if (isForbidden) return <PoliteState title="You don't have access to this student" body="Only students in your assigned classes are visible from the Staff Portal." onBack={() => navigate("/staff-portal/students")} />;
  if (isMissing) return <PoliteState title="Student not found." body="It may have been removed, or the link is out of date." onBack={() => navigate("/staff-portal/students")} />;

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", pb: 5 }}>
      {/* ── Hero (dark navy, matches admin visual grammar) ───────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3 } }}>
        <Button
          onClick={() => navigate("/staff-portal/students")}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "none", color: NAVY, fontWeight: 700, mb: 1.25 }}
        >
          My Students
        </Button>
        <Card sx={{
          borderRadius: 3, overflow: "hidden",
          background: `linear-gradient(135deg, #0f1e3c 0%, ${NAVY} 55%, ${NAVY_2} 100%)`,
          color: "#fff", position: "relative",
          "&::after": {
            content: '""', position: "absolute", inset: 0, pointerEvents: "none",
            background: "radial-gradient(600px 220px at 90% 0%, rgba(255,255,255,0.06), transparent 60%)",
          },
        }}>
          <Box sx={{ p: { xs: 2.5, md: 3 }, position: "relative", zIndex: 1 }}>
            {isLoading || !detail ? (
              <Skeleton height={72} width={320} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />
            ) : (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                <Stack direction="row" spacing={2.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.28)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 22, flexShrink: 0,
                  }}>{detail.avatar_initials || "?"}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
                      {detail.student_name}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                      {detail.grade && <Chip size="small" label={`Grade ${detail.grade}`} sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 700, fontSize: 11 }} />}
                      {detail.my_relationship.subjects.slice(0, 2).map((s) => (
                        <Chip key={s} size="small" label={s} sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#e2e8f0", fontWeight: 600, fontSize: 11 }} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() => navigate(`/staff-portal/reports/report-cards?student_id=${detail.student_id}`)}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.22)", "&:hover": { bgcolor: "rgba(255,255,255,0.20)" } }}
                  >
                    View Report Card
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => navigate("/staff-portal/messages")}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#fff", color: NAVY, "&:hover": { bgcolor: "#f1f5f9" } }}
                  >
                    Message
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Card>
      </Box>

      {/* ── KPI row (academic only — no payments) ────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <KpiCard
              label="Performance"
              value={report?.summary.overall_average !== null && report?.summary.overall_average !== undefined ? `${report.summary.overall_average}%` : "—"}
              sublabel={report && report.summary.graded_count > 0 ? `${report.summary.graded_count} graded` : "No graded assessments yet"}
              accent={NAVY} bg="#eef2ff" icon={<TrendingUpOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              label="Attendance"
              value={att && att.total > 0 ? `${att.rate}%` : "—"}
              sublabel={att && att.total > 0 ? `${att.total} sessions with me` : "No attendance yet"}
              accent="#0891b2" bg="#ecfeff" icon={<EventAvailableOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              label="Subjects"
              value={detail?.my_relationship.subjects.length ?? "—"}
              sublabel="Subjects I teach"
              accent="#7c3aed" bg="#f5f3ff" icon={<MenuBookOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              label="Classes With Me"
              value={detail?.my_relationship.occurrences_count ?? "—"}
              sublabel={detail ? `${detail.my_relationship.classes_count} class${detail.my_relationship.classes_count === 1 ? "" : "es"}` : ""}
              accent="#16a34a" bg="#f0fdf4" icon={<ClassOutlinedIcon />}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Tab strip (horizontal scroll on mobile) ──────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
        <Card sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", px: 1, py: 0.5 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons={false}
            allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none", fontWeight: 700, fontSize: 13, minHeight: 44,
                color: "#475569", gap: 0.75,
              },
              "& .MuiTab-root.Mui-selected": { color: NAVY },
              "& .MuiTabs-indicator": { backgroundColor: NAVY, height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} icon={t.icon as any} iconPosition="start" label={t.label} />
            ))}
          </Tabs>
        </Card>
      </Box>

      {/* ── Tab content ──────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
        {tab === "dashboard" && (
          <Grid container spacing={2}>
            {/* LEFT: schedule/agenda + teaching relationship */}
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                {detail && <StaffStudentCalendar studentId={detail.student_id} compact student={{ id: detail.student_id, name: detail.student_name, grade: detail.grade }} />}

                <SectionCard title="My Teaching Relationship" icon={<MenuBookOutlinedIcon fontSize="small" />}>
                  {isLoading || !detail ? (
                    <Skeleton height={40} />
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <MiniMetric label="Subjects" value={detail.my_relationship.subjects.length || "—"} />
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mt: 0.75 }}>
                          {detail.my_relationship.subjects.map((s) => (
                            <Chip key={s} label={s} size="small" sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 600, fontSize: 11 }} />
                          ))}
                        </Stack>
                      </Grid>
                      <Grid item xs={6} sm={4}><MiniMetric label="Classes" value={detail.my_relationship.classes_count} /></Grid>
                      <Grid item xs={6} sm={4}><MiniMetric label="Sessions with me" value={detail.my_relationship.occurrences_count} /></Grid>
                    </Grid>
                  )}
                </SectionCard>
              </Stack>
            </Grid>

            {/* RIGHT: quick stats + attendance + recent assessment */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <SectionCard title="Quick Stats">
                  <QuickStatRow label="Sessions with me" value={detail?.my_relationship.occurrences_count ?? "—"} />
                  <QuickStatRow label="Attendance rate" value={att && att.total > 0 ? `${att.rate}%` : "—"} valueColor={rateColor(att?.rate)} />
                  <QuickStatRow label="Subjects" value={detail?.my_relationship.subjects.length ?? "—"} />
                  <QuickStatRow label="Graded assessments" value={report?.summary.graded_count ?? "—"} last />
                </SectionCard>

                <SectionCard title="Attendance" icon={<HowToRegOutlinedIcon fontSize="small" />}>
                  {!att || att.total === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No attendance recorded in your classes yet.</Typography>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 32, fontWeight: 900, color: rateColor(att.rate), lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{att.rate}%</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700, mt: 0.5 }}>Overall rate ({att.total} records)</Typography>
                      <Divider sx={{ my: 1.25 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={4}><AttStat label="Present" value={att.present} color="#16a34a" /></Grid>
                        <Grid item xs={4}><AttStat label="Absent" value={att.absent} color="#dc2626" /></Grid>
                        <Grid item xs={4}><AttStat label="Late" value={att.late} color="#d97706" /></Grid>
                      </Grid>
                    </>
                  )}
                </SectionCard>

                <SectionCard title="Recent Assessment" icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}>
                  {!recentAssessment ? (
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No graded assessments yet.</Typography>
                  ) : (
                    <Box
                      onClick={() => navigate(`/staff-portal/assessments/${recentAssessment.assessment_id}`)}
                      role="button" tabIndex={0}
                      sx={{ cursor: "pointer", p: 1.25, borderRadius: 2, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f8fafc" } }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{recentAssessment.subject}</Typography>
                        {recentAssessment.percentage !== null && (
                          <Chip size="small" label={`${Math.round(recentAssessment.percentage)}%${recentAssessment.pass_fail !== null ? ` · ${recentAssessment.pass_fail ? "Passed" : "Below"}` : ""}`}
                            sx={{ bgcolor: recentAssessment.pass_fail === false ? "#fee2e2" : "#dcfce7", color: recentAssessment.pass_fail === false ? "#b91c1c" : "#166534", fontWeight: 800, fontSize: 10.5, height: 22 }} />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mt: 0.25 }}>{recentAssessment.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                        {recentAssessment.score !== null && recentAssessment.max_score !== null ? `${recentAssessment.score} / ${recentAssessment.max_score}` : "—"} · {fmtDate(recentAssessment.date)}
                      </Typography>
                    </Box>
                  )}
                </SectionCard>
              </Stack>
            </Grid>
          </Grid>
        )}

        {tab === "profile" && (
          <SectionCard title="Student Profile" icon={<PersonOutlineIcon fontSize="small" />}>
            {isLoading || !detail ? <Skeleton height={80} /> : (
              <Grid container spacing={2}>
                <ProfileRow label="Name" value={detail.student_name} />
                <ProfileRow label="Grade" value={detail.grade ?? "—"} />
                <ProfileRow label="Subjects with me" value={detail.my_relationship.subjects.length > 0 ? detail.my_relationship.subjects.join(", ") : "—"} />
                <ProfileRow label="Classes with me" value={detail.my_relationship.classes_count} />
                <ProfileRow label="Sessions with me" value={detail.my_relationship.occurrences_count} />
              </Grid>
            )}
          </SectionCard>
        )}

        {tab === "subjects" && (
          <SectionCard title="Subjects" icon={<MenuBookOutlinedIcon fontSize="small" />}>
            {!report ? <Skeleton height={80} /> : report.subjects.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No subjects with graded activity yet.</Typography>
            ) : (
              <Grid container spacing={2}>
                {report.subjects.map((s) => (
                  <Grid item xs={12} sm={6} md={4} key={s.subject}>
                    <Box sx={{ p: 1.75, border: "1px solid #e2e8f0", borderRadius: 2, height: "100%" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{s.subject}</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: rateColor(s.average) }}>
                          {s.average !== null ? `${s.average}%` : "—"}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                        {s.assessments === 0 ? "No graded assessments yet" : `${s.assessments} assessment${s.assessments === 1 ? "" : "s"} · Passed ${s.passed}${s.failed > 0 ? ` · Below ${s.failed}` : ""}`}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        )}

        {tab === "schedule" && detail && (
          <StaffStudentCalendar studentId={detail.student_id} student={{ id: detail.student_id, name: detail.student_name, grade: detail.grade }} />
        )}

        {tab === "attendance" && (
          <SectionCard
            title="Attendance"
            icon={<HowToRegOutlinedIcon fontSize="small" />}
            right={
              <Button size="small" onClick={() => navigate("/attendance")} sx={{ textTransform: "none", fontWeight: 700, color: NAVY_2 }}>
                View Attendance →
              </Button>
            }
          >
            {!att || att.total === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No attendance recorded in your classes yet.</Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}><AttStat label="Present" value={att.present} color="#16a34a" /></Grid>
                  <Grid item xs={6} sm={3}><AttStat label="Absent" value={att.absent} color="#dc2626" /></Grid>
                  <Grid item xs={6} sm={3}><AttStat label="Late" value={att.late} color="#d97706" /></Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Rate</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: rateColor(att.rate) }}>{att.rate}%</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Stack divider={<Divider flexItem />}>
                  {past.map((c) => <ScheduleRow key={c.occurrence_id} c={c} onOpen={() => navigate(`/class-sessions/${c.occurrence_id}/attendance`)} />)}
                </Stack>
              </>
            )}
          </SectionCard>
        )}

        {tab === "assessments" && (
          <SectionCard
            title="Assessments"
            icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}
            right={<Chip size="small" label={`${report?.summary.graded_count ?? 0} graded`} sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: 11 }} />}
          >
            {!report ? <Skeleton height={80} /> : report.assessments.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No assessments have been created yet.</Typography>
            ) : (
              <Stack divider={<Divider flexItem />}>
                {report.assessments.slice(0, 10).map((a) => (
                  <Box
                    key={a.assessment_id}
                    onClick={() => navigate(`/staff-portal/assessments/${a.assessment_id}`)}
                    sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" }, borderRadius: 1, px: 0.75 }}
                  >
                    <Box sx={{ minWidth: 100 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY }}>{fmtDate(a.date)}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{a.subject}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{a.marked && a.score !== null && a.max_score !== null ? `${a.score} / ${a.max_score}` : "Not graded"}</Typography>
                    </Box>
                    {a.marked && a.percentage !== null ? (
                      <Chip size="small" label={`${Math.round(a.percentage)}%${a.pass_fail !== null ? ` · ${a.pass_fail ? "Passed" : "Below"}` : ""}`}
                        sx={{ bgcolor: a.pass_fail === false ? "#fee2e2" : "#dcfce7", color: a.pass_fail === false ? "#b91c1c" : "#166534", fontWeight: 800, fontSize: 10.5, height: 22 }} />
                    ) : (
                      <Chip size="small" label="Not graded" sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700, fontSize: 10.5, height: 22 }} />
                    )}
                    <OpenInNewOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        )}

        {tab === "messages" && (
          <SectionCard title="Messages" icon={<ChatBubbleOutlineIcon fontSize="small" />}
            right={<Button size="small" onClick={() => navigate("/staff-portal/messages")} sx={{ textTransform: "none", fontWeight: 700, color: NAVY_2 }}>Open Messages →</Button>}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Message the student's parent/guardian from the Staff Messages workspace.
            </Typography>
          </SectionCard>
        )}

        {tab === "report-card" && (
          <SectionCard title="Report Card" icon={<DescriptionOutlinedIcon fontSize="small" />}>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
              A composed academic summary — performance, subjects, feedback, and attendance — for this student in your teaching scope.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={() => detail && navigate(`/staff-portal/reports/report-cards?student_id=${detail.student_id}`)}
              disabled={!detail}
              sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${NAVY_2}, ${NAVY})` }}
            >
              Open Full Report Card
            </Button>
          </SectionCard>
        )}
      </Box>
    </Box>
  );
};

const PoliteState: React.FC<{ title: string; body: string; onBack: () => void }> = ({ title, body, onBack }) => (
  <Box sx={{ minHeight: "60vh", p: 4, bgcolor: PAGE_BG }}>
    <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ textTransform: "none", mb: 2, fontWeight: 700, color: NAVY }}>
      Back to My Students
    </Button>
    <Card sx={{ borderRadius: 3, textAlign: "center", py: 6, maxWidth: 480, mx: "auto" }}>
      <CardContent>
        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{body}</Typography>
      </CardContent>
    </Card>
  </Box>
);

const ScheduleRow: React.FC<{ c: Detail["recent_classes"][number]; onOpen: () => void }> = ({ c, onOpen }) => (
  <Box
    onClick={onOpen}
    sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" }, px: 0.75, borderRadius: 1 }}
  >
    <Box sx={{ minWidth: 118 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: NAVY }}>{fmtDate(c.scheduled_start_at)}</Typography>
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{fmtTime(c.scheduled_start_at)} – {fmtTime(c.scheduled_end_at)}</Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#0f172a" }}>{c.class_label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{c.location || "—"}</Typography>
    </Box>
    <StatusChip status={c.status} />
  </Box>
);

const QuickStatRow: React.FC<{ label: string; value: React.ReactNode; valueColor?: string; last?: boolean }> = ({ label, value, valueColor, last }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: last ? "none" : "1px solid #f1f5f9" }}>
    <Typography sx={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color: valueColor ?? "#0f172a" }}>{value}</Typography>
  </Stack>
);

const AttStat: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{value}</Typography>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
  </Box>
);

const MiniMetric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{value}</Typography>
  </Box>
);

const ProfileRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{value}</Typography>
  </Grid>
);

export default StudentView;
